import type { AgreementFormData } from "../pages/agreement/newContract/agreementForm.types";

const normalize = (name: string) =>
  name
    // Split PascalCase / camelCase ("FirstPartyName" -> "First Party Name", "PartyBName" -> "Party B Name"),
    // the form AI-drafted template placeholders take, so they match the same keywords.
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const formatDate = (value?: string) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatCurrency = (value: AgreementFormData["agreementValue"]) => {
  if (value === undefined || value === null || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return `₹${num.toLocaleString("en-IN")}`;
};

const partyDisplayName = (party?: AgreementFormData["firstParty"]) =>
  party?.name || party?.brandName || party?.indi_name || "";

const partyAddress = (party?: AgreementFormData["firstParty"]) =>
  party?.corporateAddress || party?.regAddress || party?.indi_resAddress || "";

const partySigningAuthority = (party?: AgreementFormData["firstParty"]) => {
  const sa = party?.signingAuthority;
  if (!sa) return "";
  if (typeof sa === "string") return sa;
  return sa.label || sa.name || "";
};

/**
 * Keyword groups checked in order against each normalized placeholder name.
 * Order matters: more specific keyword sets (e.g. "second party address")
 * must be checked before their more general counterparts.
 */
const buildKeywordRules = (
  formData: AgreementFormData,
): Array<{ keywords: string[]; value: string }> => [
  {
    keywords: ["second party name", "party b name", "party 2 name"],
    value: partyDisplayName(formData.secondParty),
  },
  {
    keywords: [
      "second party address",
      "party b address",
      "second company address",
    ],
    value: partyAddress(formData.secondParty),
  },
  {
    keywords: [
      "second party signing authority",
      "second party signatory",
      "party b signatory",
    ],
    value: partySigningAuthority(formData.secondParty),
  },
  {
    keywords: [
      "first party name",
      "party a name",
      "party 1 name",
      "company name",
    ],
    value: partyDisplayName(formData.firstParty),
  },
  {
    keywords: [
      "first party address",
      "party a address",
      "first company address",
    ],
    value: partyAddress(formData.firstParty),
  },
  {
    keywords: [
      "first party signing authority",
      "first party signatory",
      "party a signatory",
    ],
    value: partySigningAuthority(formData.firstParty),
  },
  {
    keywords: ["second party", "party b", "party 2", "second company"],
    value: partyDisplayName(formData.secondParty),
  },
  {
    keywords: [
      "first party",
      "party a",
      "party 1",
      "first company",
      "client name",
      "vendor name",
    ],
    value: partyDisplayName(formData.firstParty),
  },
  {
    keywords: [
      "end date",
      "expiry date",
      "expiration date",
      "due date",
      "contract end",
      "termination date",
    ],
    value: formatDate(formData.dueDateOfContract),
  },
  {
    keywords: [
      "start date",
      "effective date",
      "commencement date",
      "contract date",
      "agreement date",
      "date of contract",
      "today s date",
    ],
    value: formatDate(formData.dateOfContract),
  },
  {
    keywords: [
      "contract value",
      "agreement value",
      "contract amount",
      "total value",
      "consideration amount",
    ],
    value: formatCurrency(formData.agreementValue),
  },
  {
    keywords: ["business unit", "department"],
    value: formData.businessUnitName || "",
  },
  {
    keywords: ["contract title", "agreement title"],
    value: formData.contractTitle || "",
  },
];

/**
 * Matches each of a template's actual placeholder names against keyword
 * groups derived from data already captured in earlier wizard steps, and
 * returns a map keyed by the EXACT placeholder name (WordEditor's replace
 * does a literal `[[key]]` match, no normalization). Placeholders with no
 * confident match are left out of the map so they stay blank for manual entry.
 */
export const computePlaceholderAutofillMap = (
  // Declared as `string[]` in Template but actually returned by the API as
  // `{ placeholderName: string }[]` — see the existing `(p: any) =>` reads
  // of this same field elsewhere in FormStep5.tsx.
  templatePlaceholders: unknown[] | undefined,
  formData: AgreementFormData,
): Record<string, string> => {
  if (!templatePlaceholders || templatePlaceholders.length === 0) return {};

  const rules = buildKeywordRules(formData);
  const map: Record<string, string> = {};

  for (const placeholder of templatePlaceholders) {
    const name = (placeholder as { placeholderName?: string })?.placeholderName;
    if (!name) continue;
    const normalized = normalize(name);

    const rule = rules.find((r) =>
      r.keywords.some((k) => normalized.includes(k)),
    );

    if (rule && rule.value) {
      map[name] = rule.value;
    }
  }

  return map;
};

/**
 * The placeholder names a contract template author is offered in "Add New
 * Placeholder". Each one matches a keyword rule above, so the contract
 * wizard fills it from what was entered in its earlier steps; any other
 * name the author types stays blank for manual entry. (A unit test keeps
 * every key here matched by a rule.)
 */
export const CONTRACT_PLACEHOLDER_FIELDS: Array<{
  key: string;
  label: string;
  example: string;
}> = [
  { key: "FirstPartyName", label: "First party name", example: "Acme Traders Pvt Ltd" },
  { key: "FirstPartyAddress", label: "First party address", example: "12 MG Road, Pune" },
  { key: "FirstPartySignatory", label: "First party signatory", example: "R. Sharma" },
  { key: "SecondPartyName", label: "Second party name", example: "Bharat Logistics LLP" },
  { key: "SecondPartyAddress", label: "Second party address", example: "45 Park Street, Kolkata" },
  { key: "SecondPartySignatory", label: "Second party signatory", example: "A. Mehta" },
  { key: "ContractTitle", label: "Contract title", example: "Master Services Agreement" },
  { key: "EffectiveDate", label: "Contract date", example: "01 October 2026" },
  { key: "EndDate", label: "Contract end date", example: "30 September 2027" },
  { key: "ContractValue", label: "Contract value", example: "₹5,00,000" },
  { key: "BusinessUnit", label: "Business unit", example: "Panipat" },
];
